import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Center,
  Tooltip,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { useCompleteExpenseBulkMutation } from "@src/__generated__/graphql";
import type { CompleteExpenseBulkDataFragment } from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { now, toApiDate } from "@src/utils/dates";
import { observer } from "mobx-react-lite";
import type { FinishProjectSummary } from "../models";
import BudgetItem from "./BudgetItem";

export interface ExpenseIncompletedProps {
  items: FinishProjectSummary["incomplete_expense_budget_items"];
  onCompleted: (items: CompleteExpenseBulkDataFragment[]) => void;
}

const ExpenseIncompleted = ({
  items,
  onCompleted,
}: ExpenseIncompletedProps) => {
  const [completeExpense, { loading }] = useCompleteExpenseBulkMutation({
    ...onError(),
    onCompleted(data) {
      // TODO Fix later
      onCompleted(
        data.completeExpenseBudgetItemBulk.map((bulk) => ({
          id: bulk.id,
          completed_at: bulk.completed_at
            ? new Date(bulk.completed_at)
            : undefined,
        })),
      );
    },
  });

  const onAction = () => {
    const ids = items.map((i) => i.id);

    if (ids.length) {
      completeExpense({
        variables: {
          ids,
          completed_at: toApiDate(now()),
        },
      });
    }
  };

  return (
    <Alert
      alignItems="top"
      rowGap="8"
      gridTemplateColumns="max-content 1fr"
      display="grid"
      status="error"
      variant="top-accent"
    >
      <AlertIcon />
      <div>
        <AlertTitle>
          <Trans>The project is missing some expenses</Trans>
        </AlertTitle>
        <AlertDescription>
          <div>
            <div className="flex items-center mb-5">
              <p>
                <Trans>
                  Budget items which doesn't have all expenses received:
                </Trans>
              </p>
              <Tooltip
                label={
                  <div
                    className="text-center p-3 text-lg"
                    style={{ maxWidth: 200 }}
                  >
                    <p>
                      <Trans>
                        Every expense budget item must be set to "all expenses
                        received".
                      </Trans>
                    </p>
                  </div>
                }
              >
                <div className="ml-3">
                  <Icon name="info-circle" color="#919EAB" variant="solid" />
                </div>
              </Tooltip>
            </div>
            <div>
              {items.map((bi) => (
                <BudgetItem key={bi.id} expenseItem={bi} />
              ))}
            </div>
          </div>
        </AlertDescription>
      </div>
      <Center gridColumn="span 2">
        <Button
          _hover={{ bg: "red.200" }}
          colorScheme="grey"
          isLoading={loading}
          onClick={() => onAction()}
          variant="outline"
        >
          <Trans>Set all expenses as received</Trans>
        </Button>
      </Center>
    </Alert>
  );
};

export default observer(ExpenseIncompleted);
